// ? NOTE:
// This is an updated version of the InteractiveCard variant contained in the stories of the Card component within our UI package. For now,
// this is being built out within the Purchasing module as there are currently no designs that make use of this pattern outside of this module.
// If that changes, and once sufficient testing/development has been done with this component, we can look to move it to the UI package. For
// now, we're keeping it here but building it in such a way that will allow for it to be easilymoved to the UI package later.

import type { MouseEvent, KeyboardEvent } from 'react'

import { Card, type ICardProps } from '@procurify/ui'

import { InteractiveField } from './components/InteractiveField'
import { InteractiveCardStyled } from './styles/InteractiveCardStyled'

export interface IInteractiveCardProps extends ICardProps {
  disabled?: boolean
  onClick: (
    e: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>
  ) => void
}

export const InteractiveCard = ({
  children,
  disabled = false,
  onClick,
  onKeyDown,
  tabIndex = 0,
  'aria-label': ariaLabel,
  ...cardProps
}: IInteractiveCardProps) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return

    // Call custom onKeyDown handler first to allow conditional prevention
    onKeyDown?.(e)

    // Handle Enter or Space to trigger onClick behavior
    // Only proceed if default wasn't prevented by custom handler
    if ((e.key === 'Enter' || e.key === ' ') && !e.defaultPrevented) {
      e.preventDefault()
      onClick?.(e)
    }
  }

  return (
    <InteractiveCardStyled disabled={disabled}>
      <Card
        {...cardProps}
        onClick={disabled ? undefined : onClick}
        onKeyDown={disabled ? undefined : handleKeyDown}
        tabIndex={disabled ? -1 : tabIndex}
        role='button'
        aria-label={ariaLabel}
        aria-disabled={disabled}
      >
        {children}
      </Card>
    </InteractiveCardStyled>
  )
}

// Export sub-components following compound component pattern
InteractiveCard.Content = Card.Content
InteractiveCard.Field = InteractiveField
// InteractiveCard.Checkbox = InteractiveCardCheckbox  // To be added later or comment removed (PUR-3360)
// InteractiveCard.Actions = InteractiveCardActions    // To be added later or comment removed (PUR-3379)
